import React from 'react';
import { SAFE_INSET } from '../../lib/printChecks';

/**
 * Screen-only guides drawn over the Print preview (never printed): the bleed ring that gets trimmed away, the trim
 * line, and the ⅛ in safe area for text. `s` = preview px per page px; the overlay covers the sheet content area
 * (page + bleed on every side when marks are on).
 */
export const PREVIEW_LAYERS_KEY = 'wp.printPreviewLayers';
const DEFAULT_LAYERS = { bleed: true, safe: false, magnify: false };
export const loadPreviewLayers = () => {
  try { return { ...DEFAULT_LAYERS, ...(JSON.parse(localStorage.getItem(PREVIEW_LAYERS_KEY) || '{}') || {}) }; } catch { return { ...DEFAULT_LAYERS }; }
};
export const savePreviewLayers = (layers) => { try { localStorage.setItem(PREVIEW_LAYERS_KEY, JSON.stringify(layers)); } catch { /* ignore */ } };

export const BLEED_COLOR = '#db2777';
export const SAFE_COLOR = '#2563eb';

const Chip = ({ color, children, style, testId }) => (
  <span className="absolute whitespace-nowrap rounded-sm px-1 text-[9px] font-semibold leading-[14px] text-white shadow-sm" style={{ background: color, ...style }} data-testid={testId}>{children}</span>
);

export const PrintPreviewGuides = ({ pageW, pageH, bleed = 0, s, layers, bleedLabel, showLabels = true }) => {
  const W = (pageW + bleed * 2) * s, H = (pageH + bleed * 2) * s, b = bleed * s;
  const safe = SAFE_INSET * s;
  const dash = `${Math.max(3, 4 * Math.min(1, s * 2))} ${Math.max(2, 3 * Math.min(1, s * 2))}`;
  return (
    <div className="absolute inset-0 pointer-events-none" data-testid="print-preview-guides" aria-hidden="true">
      <svg width={W} height={H} style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible' }}>
        {layers.bleed && bleed > 0 && (
          <g data-testid="print-preview-bleed-zone">
            <path d={`M0,0 H${W} V${H} H0 Z M${b},${b} V${H - b} H${W - b} V${b} Z`} fill={BLEED_COLOR} fillOpacity="0.16" fillRule="evenodd" />
            <rect x={b} y={b} width={pageW * s} height={pageH * s} fill="none" stroke={BLEED_COLOR} strokeWidth="1" strokeDasharray={dash} />
          </g>
        )}
        {layers.bleed && bleed === 0 && (
          <rect x={0.5} y={0.5} width={W - 1} height={H - 1} fill="none" stroke={BLEED_COLOR} strokeWidth="1" strokeDasharray={dash} data-testid="print-preview-trim-only" />
        )}
        {layers.safe && (
          <g data-testid="print-preview-safe-zone">
            <path d={`M${b},${b} H${W - b} V${H - b} H${b} Z M${b + safe},${b + safe} V${H - b - safe} H${W - b - safe} V${b + safe} Z`} fill={SAFE_COLOR} fillOpacity="0.07" fillRule="evenodd" />
            <rect x={b + safe} y={b + safe} width={pageW * s - safe * 2} height={pageH * s - safe * 2} fill="none" stroke={SAFE_COLOR} strokeWidth="1" strokeDasharray={dash} />
          </g>
        )}
      </svg>
      {showLabels && layers.bleed && bleed > 0 && <Chip color={BLEED_COLOR} style={{ left: Math.max(2, b + 4), top: Math.max(2, b - 16) }} testId="print-preview-bleed-label">bleed {bleedLabel} · trimmed away</Chip>}
      {showLabels && layers.bleed && bleed === 0 && <Chip color={BLEED_COLOR} style={{ left: 4, top: 4 }} testId="print-preview-trim-label">trim = page edge · no bleed</Chip>}
      {showLabels && layers.safe && <Chip color={SAFE_COLOR} style={{ left: b + safe + 4, top: b + safe + 4 }} testId="print-preview-safe-label">safe area · keep text inside</Chip>}
    </div>
  );
};

/** Legend for the guides toolbar. */
export const PreviewLegend = ({ layers, marksOn }) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground" data-testid="print-preview-legend">
    {layers.bleed && <span className="inline-flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-[2px]" style={{ background: BLEED_COLOR, opacity: 0.35 }} /> bleed (cut off)<span className="inline-block h-0 w-4 border-t border-dashed" style={{ borderColor: BLEED_COLOR }} /> trim line{!marksOn && ' (page edge)'}</span>}
    {layers.safe && <span className="inline-flex items-center gap-1"><span className="inline-block h-0 w-4 border-t border-dashed" style={{ borderColor: SAFE_COLOR }} /> ⅛ in safe area</span>}
    <span className="inline-flex items-center gap-1"><span className="inline-block h-0 w-4 border-t border-black" /> printed marks</span>
  </div>
);
